/**
 * Authentication Configuration for Rider Gateway (Mobile)
 * Based on proven OAuth PKCE implementation from oauth-pkce-test/
 * Optimized for mobile usage patterns
 */

export interface AuthConfig {
  cognitoDomain: string;
  userPoolId: string;
  clientId: string;
  region: string;
  redirectUri: string;
  scopes: string[];
}

export interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  tokenType: string;
  expiresIn: number;
  expiresAt: number;
}

import type { BackendUserType } from '../../types/user';

export interface JWTUser {
  sub: string;
  email: string;
  email_verified: boolean;
  'custom:user_type': BackendUserType;
  'cognito:groups'?: string[];
  given_name?: string;
  family_name?: string;
  phone_number?: string;
  'custom:full_name'?: string;
}

// Environment variables validation
const requiredEnvVars = {
  cognitoDomain: import.meta.env.VITE_COGNITO_DOMAIN,
  userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
  region: import.meta.env.VITE_AWS_REGION,
} as const;

// Validate all required environment variables are present
Object.entries(requiredEnvVars).forEach(([key, value]) => {
  if (!value) {
    throw new Error(`Missing required environment variable: VITE_${key.toUpperCase()}`);
  }
});

// The OAuth callback is always <current origin>/callback. Production is
// https://rider.app.gogoronp-nebula.com and local development is
// http://localhost:<port>; both are registered on the Cognito riders client
// (infrastructure/terraform/cognito.tf), and there is no other environment.
const getRedirectUri = (): string => `${window.location.origin}/callback`;

export const authConfig: AuthConfig = {
  cognitoDomain: requiredEnvVars.cognitoDomain,
  userPoolId: requiredEnvVars.userPoolId,
  clientId: requiredEnvVars.clientId, // Mobile-specific client ID
  region: requiredEnvVars.region,
  redirectUri: getRedirectUri(),
  scopes: [
    'openid',
    'profile', 
    'email',
    'nebula-api/battery.read',
    'nebula-api/swap.write',
    'nebula-api/payment.read',
    'nebula-api/payment.write',
    // Note: No admin.full scope for mobile users
  ],
};

// OAuth endpoints
export const authEndpoints = {
  authorize: `${authConfig.cognitoDomain}/oauth2/authorize`,
  token: `${authConfig.cognitoDomain}/oauth2/token`,
  logout: `${authConfig.cognitoDomain}/logout`,
  jwks: `https://cognito-idp.${authConfig.region}.amazonaws.com/${authConfig.userPoolId}/.well-known/jwks.json`,
};

// Token storage keys (mobile-specific)
export const storageKeys = {
  accessToken: 'nebula_mobile_access_token',
  idToken: 'nebula_mobile_id_token',
  refreshToken: 'nebula_mobile_refresh_token',
  codeVerifier: 'nebula_mobile_code_verifier',
  state: 'nebula_mobile_oauth_state',
  user: 'nebula_mobile_user',
  biometricEnabled: 'nebula_mobile_biometric_enabled',
  rememberDevice: 'nebula_mobile_remember_device',
} as const;

// Mobile-specific authentication settings
export const mobileAuthSettings = {
  // Token expiration buffer for mobile (refresh 5 minutes early)
  tokenRefreshBuffer: 5 * 60 * 1000, // 5 minutes in milliseconds
  
  // Maximum token cache time for offline mode
  maxOfflineCacheTime: 24 * 60 * 60 * 1000, // 24 hours
  
  // Touch targets minimum size (following iOS/Android guidelines)
  minTouchTargetSize: 44, // 44px minimum
  
  // Mobile OAuth timeout (mobile networks can be slower)
  oauthTimeout: 30000, // 30 seconds
  
  // Automatic logout on app backgrounding (security)
  autoLogoutOnBackground: false, // Set to true for high security
  
  // Enable offline mode for cached data
  enableOfflineMode: true,
};

// Authentication error types
export const AuthError = {
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  NETWORK_ERROR: 'NETWORK_ERROR',
  INVALID_STATE: 'INVALID_STATE',
  OAUTH_ERROR: 'OAUTH_ERROR',
  REFRESH_FAILED: 'REFRESH_FAILED',
  LOGOUT_FAILED: 'LOGOUT_FAILED',
  OFFLINE_MODE: 'OFFLINE_MODE',
  BIOMETRIC_FAILED: 'BIOMETRIC_FAILED',
} as const;

export type AuthError = typeof AuthError[keyof typeof AuthError];

export interface AuthErrorInfo {
  type: AuthError;
  message: string;
  details?: any;
}

// Mobile-specific OAuth parameters
export const mobileOAuthParams = {
  // PKCE parameters for mobile security
  codeChallengeMethod: 'S256',
  
  // Mobile-friendly response mode
  responseMode: 'query', // Better than fragment for mobile
  
  // State parameter length (shorter for mobile URLs)
  stateLength: 16,
  
  // Code verifier length (RFC 7636 compliant)
  codeVerifierLength: 32,
};